import { computePointsAreaHa, computePointsPerimeterM } from "../utils/mapMeasurements";
import {
  measureTapeAcrossTracks,
  projectOntoBearing,
  snapToNearestTrack,
} from "../utils/mapTrackMeasureUtils";

const TAPE_COLOR = "#FF6F00";
const AREA_STROKE = "#1565C0";
const AREA_FILL = "#42A5F5";

function latLngOf(event) {
  if (!event || !event.latLng) return null;
  return { lat: event.latLng.lat(), lng: event.latLng.lng() };
}

export function createTrackMeasureController({ onChange } = {}) {
  let google = null;
  let getLines = () => [];
  let tool = null;
  let draftPoints = [];
  let previewPoint = null;
  let tapeAxis = null;
  let result = null;
  let overlays = [];
  let listeners = [];

  function emit() {
    if (typeof onChange === "function") {
      onChange(getState());
    }
  }

  function getState() {
    return {
      tool,
      draftCount: draftPoints.length,
      previewPoint,
      snapped: Boolean(tapeAxis && tapeAxis.snapped),
      result,
    };
  }

  function clearOverlays() {
    overlays.forEach((overlay) => {
      if (overlay && overlay.setMap) overlay.setMap(null);
    });
    overlays = [];
  }

  function addOverlay(overlay) {
    if (overlay) overlays.push(overlay);
    return overlay;
  }

  function currentPoints() {
    if (!previewPoint) return draftPoints.slice();
    return draftPoints.concat([previewPoint]);
  }

  function drawDraft() {
    if (!google || !google.maps || !google.map) return;
    clearOverlays();
    const maps = google.maps;
    const points = currentPoints();

    if (tool === "tape" && points.length >= 2) {
      addOverlay(
        new maps.Polyline({
          path: points,
          map: google.map,
          strokeColor: TAPE_COLOR,
          strokeOpacity: 1,
          strokeWeight: 3,
          zIndex: 40,
          clickable: false,
        })
      );
    }

    if (tool === "area" && points.length >= 2) {
      if (points.length >= 3) {
        addOverlay(
          new maps.Polygon({
            paths: points,
            map: google.map,
            strokeColor: AREA_STROKE,
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: AREA_FILL,
            fillOpacity: 0.22,
            zIndex: 35,
            clickable: false,
          })
        );
      } else {
        addOverlay(
          new maps.Polyline({
            path: points,
            map: google.map,
            strokeColor: AREA_STROKE,
            strokeOpacity: 1,
            strokeWeight: 2,
            zIndex: 35,
            clickable: false,
          })
        );
      }
    }

    points.forEach((point, index) => {
      addOverlay(
        new maps.Marker({
          position: point,
          map: google.map,
          clickable: false,
          zIndex: 50,
          label: {
            text: String(index + 1),
            color: "#fff",
            fontSize: "11px",
            fontWeight: "700",
          },
          icon: {
            path: maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: tool === "tape" ? TAPE_COLOR : AREA_STROKE,
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 1,
          },
        })
      );
    });

    if (result && result.kind === "tape" && result.crossings) {
      result.crossings.forEach((crossing) => {
        addOverlay(
          new maps.Marker({
            position: { lat: crossing.lat, lng: crossing.lng },
            map: google.map,
            clickable: false,
            zIndex: 55,
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: 5,
              fillColor: "#111",
              fillOpacity: 0.9,
              strokeColor: "#FFD54F",
              strokeWeight: 2,
            },
          })
        );
      });
    }
  }

  function finishTape(endPoint) {
    const start = draftPoints[0];
    const end = endPoint || draftPoints[1];
    if (!start || !end) return;
    const measurement = measureTapeAcrossTracks(start, end, getLines(), {
      snapped: Boolean(tapeAxis && tapeAxis.snapped),
      tapeBearing: tapeAxis && tapeAxis.bearing,
    });
    result = { kind: "tape", start, end, ...measurement };
    draftPoints = [start, end];
    previewPoint = null;
    drawDraft();
    emit();
  }

  function finishArea() {
    if (draftPoints.length < 3) return;
    result = {
      kind: "area",
      points: draftPoints.slice(),
      areaHa: computePointsAreaHa(draftPoints, google && google.maps),
      perimeterM: computePointsPerimeterM(draftPoints, google && google.maps),
      vertexCount: draftPoints.length,
    };
    previewPoint = null;
    drawDraft();
    emit();
  }

  function consumeClick(point) {
    if (!tool || !point) return false;

    if (tool === "tape") {
      if (!draftPoints.length) {
        const snap = snapToNearestTrack(point, getLines());
        if (snap) {
          tapeAxis = { origin: snap.point, bearing: snap.tapeBearing, snapped: true };
          draftPoints = [snap.point];
        } else {
          tapeAxis = { origin: point, bearing: null, snapped: false };
          draftPoints = [point];
        }
        result = null;
        previewPoint = null;
        drawDraft();
        emit();
        return true;
      }
      const end =
        tapeAxis && tapeAxis.snapped
          ? projectOntoBearing(tapeAxis.origin, tapeAxis.bearing, point)
          : point;
      finishTape(end);
      return true;
    }

    if (tool === "area") {
      if (result) {
        result = null;
        draftPoints = [];
      }
      draftPoints = draftPoints.concat([point]);
      previewPoint = null;
      drawDraft();
      emit();
      return true;
    }

    return false;
  }

  function consumeMove(point) {
    if (!tool || !point || !draftPoints.length || result) return false;
    if (tool === "tape" && tapeAxis && tapeAxis.snapped) {
      previewPoint = projectOntoBearing(tapeAxis.origin, tapeAxis.bearing, point);
    } else {
      previewPoint = point;
    }
    if (tool === "tape" && draftPoints.length === 1 && previewPoint) {
      result = {
        kind: "tape",
        ...measureTapeAcrossTracks(draftPoints[0], previewPoint, getLines(), {
          snapped: Boolean(tapeAxis && tapeAxis.snapped),
          tapeBearing: tapeAxis && tapeAxis.bearing,
        }),
      };
    }
    drawDraft();
    emit();
    return true;
  }

  function consumeDblClick(point) {
    if (tool !== "area") return false;
    if (point) consumeClick(point);
    finishArea();
    return true;
  }

  function detachListeners() {
    listeners.forEach((listener) => {
      if (google && google.maps && google.maps.event && listener) {
        google.maps.event.removeListener(listener);
      }
    });
    listeners = [];
  }

  function attachListeners() {
    detachListeners();
    if (!google || !google.map || !google.maps) return;
    const maps = google.maps;
    listeners.push(
      maps.event.addListener(google.map, "click", (event) => {
        consumeClick(latLngOf(event));
      })
    );
    listeners.push(
      maps.event.addListener(google.map, "mousemove", (event) => {
        consumeMove(latLngOf(event));
      })
    );
    listeners.push(
      maps.event.addListener(google.map, "dblclick", (event) => {
        if (tool === "area") {
          if (event && event.stop) event.stop();
          consumeDblClick(latLngOf(event));
        }
      })
    );
  }

  function setCursor() {
    if (!google || !google.map) return;
    google.map.setOptions({
      draggableCursor: tool ? "crosshair" : null,
      disableDoubleClickZoom: tool === "area",
    });
  }

  return {
    attach(nextGoogle, nextGetLines) {
      google = nextGoogle;
      if (typeof nextGetLines === "function") getLines = nextGetLines;
      attachListeners();
      setCursor();
      drawDraft();
    },
    setGetLines(nextGetLines) {
      if (typeof nextGetLines === "function") getLines = nextGetLines;
    },
    startTool(nextTool) {
      tool = nextTool;
      draftPoints = [];
      previewPoint = null;
      tapeAxis = null;
      result = null;
      setCursor();
      drawDraft();
      emit();
    },
    finishArea,
    clear() {
      tool = null;
      draftPoints = [];
      previewPoint = null;
      tapeAxis = null;
      result = null;
      clearOverlays();
      setCursor();
      emit();
    },
    consumeClick,
    consumeMove,
    isActive() {
      return Boolean(tool);
    },
    getState,
    destroy() {
      detachListeners();
      clearOverlays();
      google = null;
    },
  };
}
